import { useQuery } from '@tanstack/react-query';
import { PhoneIncoming, PhoneMissed, PhoneOutgoing } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  fetchCallRecordingObjectUrl,
  fetchCallsForConversation,
  fetchCallsForLead,
  type WhatsAppCall,
} from './whatsapp-calls.api';

/**
 * WhatsApp voice call log for one thread
 * (docs/integrations/doubletick-whatsapp-calling.md).
 *
 * Read-only: rows are built by the CALL_* webhooks, so this never invents a
 * status. Recordings arrive minutes after a call ends, in their own webhook,
 * which is why a just-ended call shows no player yet.
 */

const STATUS_LABEL: Record<string, string> = {
  INITIATED: 'Ringing',
  IN_PROGRESS: 'In progress',
  END: 'Completed',
  REJECT: 'Declined',
  MISSED: 'Missed',
  FAILED: 'Failed',
};

/** Missed/declined/failed read as problems; everything else is neutral. */
const isUnanswered = (status: string): boolean =>
  status === 'MISSED' || status === 'REJECT' || status === 'FAILED';

const formatDuration = (seconds: number | null): string => {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Recordings are fetched through the API (the provider 403s without the
 * account key) and only when the agent asks, so opening a thread with a long
 * call history does not pull down every audio file.
 */
const RecordingPlayer = ({ callId }: { callId: string }): React.JSX.Element => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Object URLs are revoked on unmount - a widget that opens and closes all
    // day would otherwise hold every recording it ever played in memory.
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (url) return <audio controls src={url} className="h-8 w-full" />;

  return (
    <button
      type="button"
      disabled={loading}
      onClick={() => {
        setLoading(true);
        fetchCallRecordingObjectUrl(callId)
          .then(setUrl)
          .catch(() => setError(true))
          .finally(() => setLoading(false));
      }}
      className="text-xs text-sky-700 underline underline-offset-2 disabled:opacity-50"
    >
      {loading ? 'Loading…' : error ? 'Recording unavailable' : 'Play recording'}
    </button>
  );
};

export const WhatsAppCallHistory = ({
  leadId,
  conversationId,
}: {
  leadId?: string;
  conversationId?: string;
}): React.JSX.Element => {
  const query = useQuery({
    queryKey: ['whatsapp-calls', leadId ?? conversationId],
    queryFn: () =>
      leadId ? fetchCallsForLead(leadId) : fetchCallsForConversation(conversationId ?? ''),
    enabled: Boolean(leadId ?? conversationId),
  });

  if (query.isLoading) {
    return <p className="p-3 text-sm text-zinc-500">Loading calls…</p>;
  }

  const calls: WhatsAppCall[] = query.data ?? [];
  if (calls.length === 0) {
    return <p className="p-3 text-sm text-zinc-500">No WhatsApp calls on this thread yet.</p>;
  }

  return (
    <ul className="divide-y divide-zinc-200">
      {calls.map((call) => {
        const unanswered = isUnanswered(call.status);
        const Icon = unanswered
          ? PhoneMissed
          : call.direction === 'INCOMING'
            ? PhoneIncoming
            : PhoneOutgoing;
        return (
          <li key={call.id} className="flex items-start gap-2.5 px-3 py-2">
            <Icon
              className={`mt-0.5 h-4 w-4 shrink-0 ${unanswered ? 'text-rose-600' : 'text-emerald-600'}`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-zinc-900">
                {call.direction === 'INCOMING' ? 'Incoming' : 'Outgoing'}
                {' · '}
                <span className={unanswered ? 'text-rose-600' : 'text-zinc-600'}>
                  {STATUS_LABEL[call.status] ?? call.status}
                </span>
                {call.durationSeconds ? ` · ${formatDuration(call.durationSeconds)}` : ''}
              </p>
              <p className="text-[11px] text-zinc-500">
                {new Date(call.startedAt ?? call.createdAt).toLocaleString()}
                {call.failedReason ? ` · ${call.failedReason}` : ''}
              </p>
              {(call.recordingKey || call.recordingUrl) && (
                <div className="mt-1">
                  <RecordingPlayer callId={call.callId} />
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
